import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { PATHS } from '@shared/constants/paths';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { ImportOrdersStoreService } from '@usf/ngrx-order';
import { ImportOrderTransaction } from '@usf/ngrx-order/lib/reducers/import-orders/import-orders.reducer';
import { IMPORT_ORDER_VALIDATION_TRACING_ACTIONS } from '../pages/import-order-validation/tracking/tracing/import-order-validation-tracing.actions';
import { ProductService } from '@shared/services/product/product.service';
import { Product, ProductPropertiesEnum } from '@usf/product-types';
import {
  createPageLoadAnalytics,
  createPageLoadImportTrace,
} from '../pages/import-order-validation/tracking/factories/import-order-validation-factories';
import { IMPORT_ORDER_VALIDATION_ANALYTICS_ACTIONS } from '../pages/import-order-validation/tracking/analytics/import-order-validation-analytics.actions';

@Injectable({
  providedIn: 'root',
})
export class ImportOrderValidationService {
  constructor(
    private importOrdersStoreService: ImportOrdersStoreService,
    private navigationHelper: NavigationHelperService,
    private store: Store,
  ) {}

  orders$ = this.importOrdersStoreService.orders$;
  loading$ = this.importOrdersStoreService.loading$;
  error$ = this.importOrdersStoreService.error$;
  fileImportErrors$ = this.importOrdersStoreService.fileImportErrors$;

  submit(orders: ImportOrderTransaction[]) {
    const importOrderIdentifier = orders[0].importOrderIdentifier;
    this.importOrdersStoreService.submit(orders, {
      importOrderIdentifier,
      orderCount: this.calcNumberOfExpectedOrders(orders),
      ordersLoaded: false,
    });
  }

  /**
   * Business rules to calculate the number of orders to expect on the imported orders page
   * @param orders Array of ImportOrderTransaction
   * @returns The number of orders to expect
   */
  calcNumberOfExpectedOrders(orders: ImportOrderTransaction[]): number {
    let numberOfExpectedOrders = 0;
    if (!!orders?.length) {
      orders.forEach(order => {
        if (!!order?.orderItems?.length) {
          numberOfExpectedOrders++;
        }
      });
    }
    return numberOfExpectedOrders;
  }

  cancel() {
    this.store.dispatch(IMPORT_ORDER_VALIDATION_TRACING_ACTIONS.cancelClick());
    this.navigationHelper.routeTo(PATHS.MY_ORDERS_VIEW);
  }

  isRoutedProduct(product: Product): boolean {
    return (
      ['0', '2', '3', '4', '8', '9'].includes(
        product?.inventory?.productStatus,
      ) ||
      (product?.inventory?.productStatus === '1' &&
        !this.isSpecialVendor(product))
    );
  }

  isSpecialVendor(product: Product): boolean {
    return !!product?.summary.properties?.has(
      ProductPropertiesEnum.specialVendor,
    );
  }

  tracePageLoad(orderCount: number) {
    const tracking = createPageLoadImportTrace();
    (tracking.tracing.data.attributes
      .order as any).importedOrdersCount = orderCount;
    this.store.dispatch(
      IMPORT_ORDER_VALIDATION_TRACING_ACTIONS.traceImportOrderValidationPageLoad(
        {
          tracking,
        },
      ),
    );
  }

  trackPageLoadAnalytics() {
    const tracking = createPageLoadAnalytics();
    this.store.dispatch(
      IMPORT_ORDER_VALIDATION_ANALYTICS_ACTIONS.pageLoad({ tracking }),
    );
  }
}
